import React, { useState, useEffect } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import * as XLSX from 'xlsx';
import "./Order.css";
import NotFoundImage from "./not-found.png";

const OrderManager = () => {
  const [orders, setOrders] = useState([]);
  const [filteredOrders, setFilteredOrders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("");
  const [selectedOrders, setSelectedOrders] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState("");
  const [selectedYear, setSelectedYear] = useState("");
  const [availableMonths, setAvailableMonths] = useState([]);
  const [availableYears, setAvailableYears] = useState([]);
  const [availableMonthsForYear, setAvailableMonthsForYear] = useState([]);
  const API_URL = process.env.REACT_APP_API_URL;

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await axios.get(`${API_URL}/order/allOrders`);
      setOrders(response.data);
      setFilteredOrders(response.data);

      // Get unique months and years from the orders
      const months = [];
      const years = [];
      response.data.forEach((order) => {
        const orderDate = new Date(order.purchasedDate);
        const month = orderDate.getMonth();
        const year = orderDate.getFullYear();

        if (!months.includes(month)) months.push(month);
        if (!years.includes(year)) years.push(year);
      });

      setAvailableMonths(months);
      setAvailableYears(years);
    } catch (error) {
      console.error("Error fetching orders", error);
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    setFilteredOrders(
      orders.filter(
        (order) =>
          order.studentId.toLowerCase().includes(query) ||
          order.Address.name.toLowerCase().includes(query) ||
          order.Address.phone.includes(query)
      )
    );
  };

  const handleSortChange = (e) => {
    const sortValue = e.target.value;
    setSortOption(sortValue);
    
    // Reset filters when changing sort option
    setFilteredOrders(orders);
    setSelectedMonth("");
    setSelectedYear("");
    setAvailableMonthsForYear([]);
  };

  const handleYearChange = (e) => {
    const year = parseInt(e.target.value);
    setSelectedYear(year);
    setSelectedMonth(""); // Reset month when year changes
    
    if (isNaN(year) || year === "") {
      setFilteredOrders(orders);
      setAvailableMonthsForYear([]);
      return;
    }

    // Filter orders by selected year
    const yearFilteredOrders = orders.filter((order) => {
      const orderDate = new Date(order.purchasedDate);
      return orderDate.getFullYear() === year;
    });

    // Get available months for selected year
    const monthsInYear = new Set(
      yearFilteredOrders.map((order) => new Date(order.purchasedDate).getMonth())
    );
    setAvailableMonthsForYear(Array.from(monthsInYear).sort((a, b) => a - b));
    
    setFilteredOrders(yearFilteredOrders);
  };

  const handleMonthChange = (e) => {
    const month = parseInt(e.target.value);
    setSelectedMonth(month);
    
    if (isNaN(month) || month === "") {
      // If no month selected, show all orders for selected year
      const yearFilteredOrders = orders.filter((order) => {
        const orderDate = new Date(order.purchasedDate);
        return orderDate.getFullYear() === selectedYear;
      });
      setFilteredOrders(yearFilteredOrders);
      return;
    }

    // Filter orders by selected year and month
    const filteredResult = orders.filter((order) => {
      const orderDate = new Date(order.purchasedDate);
      return (
        orderDate.getFullYear() === selectedYear &&
        orderDate.getMonth() === month
      );
    });

    setFilteredOrders(filteredResult);
  };

  const handleOrderCheckboxChange = (orderId) => {
    setSelectedOrders((prevSelectedOrders) => {
      if (prevSelectedOrders.includes(orderId)) {
        return prevSelectedOrders.filter((id) => id !== orderId);
      } else {
        return [...prevSelectedOrders, orderId];
      }
    });
  };

  const handleDeleteOrders = async () => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to delete these orders?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete them!",
        cancelButtonText: "No, keep them",
      });

      if (confirmation.isConfirmed) {
        // Make a request to delete selected orders
        await axios.delete(`${API_URL}/order/deleteOrder`, {
          data: { orderIds: selectedOrders },
        });

        // Show SweetAlert on success
        Swal.fire({
          icon: "success",
          title: "Orders Deleted!",
          text: "The selected orders have been deleted successfully.",
        });

        // Remove the deleted orders from the state
        setFilteredOrders(
          filteredOrders.filter((order) => !selectedOrders.includes(order._id))
        );
        setSelectedOrders([]);
      }
    } catch (error) {
      console.error("Error deleting orders", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "There was an error deleting the orders.",
      });
    }
  };

  const handleDeleteOrder = async (orderId) => {
    try {
      const confirmation = await Swal.fire({
        title: "Are you sure?",
        text: "Do you really want to delete this order?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonText: "Yes, delete it!",
        cancelButtonText: "No, keep it",
      });

      if (confirmation.isConfirmed) {
        // Make a request to delete the individual order
        await axios.delete(`${API_URL}/order/deleteOrder`, {
          data: { orderIds: [orderId] }, // Delete the individual order
        });

        // Show SweetAlert on success
        Swal.fire({
          icon: "success",
          title: "Order Deleted!",
          text: "The order has been deleted successfully.",
        });

        // Remove the deleted order from the state
        setFilteredOrders(
          filteredOrders.filter((order) => order._id !== orderId)
        );
      }
    } catch (error) {
      console.error("Error deleting order", error);
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "There was an error deleting the order.",
      });
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const downloadExcel = () => {
    const dataToExport = filteredOrders.map(order => ({
      'Student ID': order.studentId,
      'Name': order.Address.name,
      'Phone': order.Address.phone,
      'Address': `${order.Address.addressLine} ${order.Address.city} ${order.Address.state} ${order.Address.postalCode}`,
      'Total Amount': order.totalAmount,
      'Order Date': formatDate(order.purchasedDate),
      'Items': order.Items.map(item => `${item.name} (x${item.quantity})`).join(', ')
    }));

    const ws = XLSX.utils.json_to_sheet(dataToExport);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Orders');
    XLSX.writeFile(wb, 'orders.xlsx');
  };

  return (
    <div className="admin-materials-container">
      <h1>Order Management</h1>
      <div className="admin-materials-search-container">
        <input
          type="text"
          placeholder="Search by Student ID, Name, or Phone Number..."
          value={searchQuery}
          onChange={handleSearchChange}
          className="admin-order-search"
        />

        <select
          value={sortOption}
          onChange={handleSortChange}
          className="admin-materials-btn-primary"
        >
          <option value="">Sort by</option>
          <option value="year">Year</option>
        </select>

        {sortOption === "year" && (
          <>
            <select
              value={selectedYear}
              onChange={handleYearChange}
              className="admin-materials-btn-primary"
            >
              <option value="">Select Year</option>
              {Array.from(
                new Set(
                  orders.map((order) => 
                    new Date(order.purchasedDate).getFullYear()
                  )
                )
              )
              .sort((a, b) => b - a)
              .map((year) => (
                <option key={year} value={year}>
                  {year}
                </option>
              ))}
            </select>

            {selectedYear && availableMonthsForYear.length > 0 && (
              <select
                value={selectedMonth}
                onChange={handleMonthChange}
                className="admin-materials-btn-primary"
              >
                <option value="">All Months</option>
                {availableMonthsForYear.map((month) => (
                  <option key={month} value={month}>
                    {new Date(0, month).toLocaleString("default", { month: "long" })}
                  </option>
                ))}
              </select>
            )}
          </>
        )}

        <button 
          onClick={downloadExcel}
          className="admin-materials-btn-primary"
          style={{ marginLeft: '10px' }}
        >
          Download Excel
        </button>
      </div>

      {/* Button to delete selected orders */}
      {selectedOrders.length > 0 && (
        <button
          onClick={handleDeleteOrders}
          className="admin-materials-btn-danger"
        >
          Delete Selected Orders
        </button>
      )}

      <div className="admin-materials-table-wrapper">
        <table className="admin-materials-table">
          <thead>
            <tr>
              <th>
                <input
                  type="checkbox"
                  onChange={() => {
                    if (selectedOrders.length === filteredOrders.length) {
                      setSelectedOrders([]);
                    } else {
                      setSelectedOrders(
                        filteredOrders.map((order) => order._id)
                      );
                    }
                  }}
                  checked={selectedOrders.length === filteredOrders.length}
                />
              </th>
              <th>Student ID</th>
              <th>Name</th>
              <th>Phone Number</th>
              <th>Address</th>
              <th>Total</th>
              <th>Order Date</th>
              <th>Items</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredOrders.length > 0 ? (
              filteredOrders.map((order) => (
                <tr key={order._id}>
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedOrders.includes(order._id)}
                      onChange={() => handleOrderCheckboxChange(order._id)}
                    />
                  </td>
                  <td>{order.studentId}</td>
                  <td>{order.Address.name}</td>
                  <td>{order.Address.phone}</td>
                  <td>
                    {`${order.Address.addressLine} ${order.Address.city} ${order.Address.state} ${order.Address.postalCode}`}
                  </td>
                  <td>${order.totalAmount}</td>
                  <td>{formatDate(order.purchasedDate)}</td>
                  <td>
                    <ul>
                      {order.Items.map((item, index) => (
                        <li key={index} className="order-item">
                          {/* Display the name and image in the same row */}
                          <span>{item.name} </span>
                          <span className="item-quantity">(x{item.quantity})</span> {/* Display item quantity */}
                          <img
                            src={item.imageUrl}
                            alt={item.name}
                            className="admin-materials-banner-img"
                          />
                          <div className="image-url">
                            <p>
                              Image:{" "}
                              <a
                                href={item.imageUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                              >
                                View
                              </a>
                            </p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </td>
                  <td>
                    <button
                      onClick={() => handleDeleteOrder(order._id)}
                      className="admin-materials-btn-danger"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="no-results">
                  <img
                    src={NotFoundImage}
                    alt="No results"
                    className="no-results-image"
                  />
                  <p>No orders found</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default OrderManager;